<script setup lang="ts">
import {ref} from "vue";
import CustomButton from "@/components/Button/Button.vue";
import {useI18n} from "vue-i18n";
import {useHomepageStore} from "@/stores/homepageStore";
import * as AssetUtils from "@/helpers/assetsUtils";

const {t} = useI18n();

const homepageStore = useHomepageStore();
const currentFocusIndex = ref(-1);
const props = defineProps({
  'tabindex':Number
})
function selectCategory(index,category) {
  homepageStore.selections.category = category
  currentFocusIndex.value = index
}

function clickedParent() {
  homepageStore.current = 'boat';
}

function handleKeydown(event) {
  if (!homepageStore.categories.length) return;
  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      if (currentFocusIndex.value < homepageStore.categories.length - 1) {
        currentFocusIndex.value = currentFocusIndex.value + 1;
      } else {
        currentFocusIndex.value = 0;
      }
      break;
    case "ArrowUp":
      event.preventDefault();
      if (currentFocusIndex.value > 0) {
        currentFocusIndex.value = currentFocusIndex.value - 1
      } else {
        currentFocusIndex.value = homepageStore.categories.length - 1;
      }
      break;
    case "Enter":
      if(currentFocusIndex.value != -1){
        selectCategory(currentFocusIndex.value, homepageStore.categories[currentFocusIndex.value]);
        homepageStore.next()
      }
      break;
    case "Escape":
      event.preventDefault();
      homepageStore.current = 'none'
      break;
  }
}

function clearSelection() {
  homepageStore.selections.category = homepageStore.selections.category ?? {}; // Keep existing logic
  homepageStore.next(); // Keep existing logic
  currentFocusIndex.value = -1; // Remove hover effect by resetting focus
}

</script>

<template>
  <div class="relative w-1/2 h-full">
    <slot name="display" :homepage="homepageStore" :clickedParent="clickedParent">
      <div id="boatType"
           @click="clickedParent"
           :tabindex="props.tabindex"
           @keydown="handleKeydown"
           class="flex flex-col justify-between py-4.5 pl-7.5 w-full h-full cursor-pointer">
        <div class="text-black/65 flex flex-row items-center gap-2  text-xs">
          <img src="@/assets/icons/svg/Boat_16x16.svg" alt="Destination">
          <span>{{ t('boat.typeAndLayout') }}</span>
        </div>
        <div class="text-black responsive-title-5 flex flex-row items-center">
          <div class="mr-6">{{ homepageStore.selections.category?.name || t('boat.selectOption') }}</div>
        </div>
      </div>
    </slot>
    <transition name="slight-scroll">
      <div v-if="homepageStore.current == 'boat'"
           class="rounded-b-11xl shadow-2xl border-t-2 border-primary-blue absolute top-full right-0 w-full bg-white flex flex-col z-20">
        <div class="h-fit flex flex-row gap-7 py-5 px-7.5">
          <div class="w-full flex flex-col gap-2 max-h-full overflow-y-scroll">
            <div class=" responsive-body-xsmall mb-3">{{ t('boat.selectType') }}</div>
            <div v-for="(category, index) in homepageStore.categories" :key="category.id"
                 :class="`boat-type bg-background-light font-semibold
                 responsive-body-small px-3.5 py-2.5 type select-none cursor-pointer rounded-8xl border-2 border-transparent
                 ${homepageStore.selections.category?.id === category.id ? 'selected-type' : ''} ${currentFocusIndex == index ? 'hover-type' : ''}`"
                 @click="selectCategory(index,category)">
              <div class="flex align-center gap-3">
                <img class="boat-icon w-8" :src="AssetUtils.getCategoryImage(category.id)" :alt="category.name">
                <div class="responsive-body-small">{{ category.name }}</div>
              </div>
            </div>
          </div>
        </div>
        <div
            class="from-gradients-ghostWhite to-gradients-aliceBlue bg-gradient-to-tr w-full flex justify-between rounded-b-11xl py-4.5 px-5">
            <CustomButton class="hover:bg-black hover:text-white hover:border-black"
                          @click="() => { homepageStore.selections.category = {}; currentFocusIndex = -1; }"
                          :outlined="false"
                          type="black"
                          :rounded="true">
              {{ t('common.clear') }}
            </CustomButton>
            <CustomButton class=" hover:bg-black hover:text-white hover:border-black"
                          @click="homepageStore.selections.category = homepageStore.selections.category ?? {}; homepageStore.next();"
                          :outlined="false"
                          type="black"
                          :rounded="true">
              {{ t('common.next') }}
            </CustomButton>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>

.boat-type:hover {
  background-color: #ebf5ff;
}

.hover-type {
  background-color: #ebf5ff;
}

.selected-type {
  border-color: #1b84ff !important;
}

@media (max-width: 1536px) {
  .boat-icon {
    width: 1.5rem !important;
  }
}
</style>